/**
 * Printable stage package generation.
 *
 * The PDF is intended for match directors building a stage in a bay: first a
 * selected static view from the designer, then briefing/count mechanics and a
 * construction blueprint page.
 */

import React from 'react';
import {
  Document,
  Page,
  View,
  Text,
  Image,
  StyleSheet,
  pdf,
} from '@react-pdf/renderer';
import type { StageLayout, StageObject } from '@/stage/types';
import { downloadBlob } from './png-export';

interface PdfOptions {
  pageSize: 'A4' | 'LETTER';
  matchName?: string;
  sourceView?: '2d' | '3d';
  blueprintImageDataUrl?: string;
}

interface CountSummary {
  paper: number;
  steel: number;
  noShoots: number;
  movers: number;
  requiredShots: number;
  targetRows: TargetRow[];
}

interface TargetRow {
  id: string;
  target: string;
  shots: string;
  notes: string;
}

const MOVING_TARGETS = new Set([
  'swinger',
  'drop_turner',
  'texas_star',
  'bobber',
  'paper_swinger',
  'paper_swinger_once',
  'paper_popup',
]);

const PAPER_TARGETS = new Set([
  'paper_target',
  'swinger',
  'drop_turner',
  'bobber',
  'paper_swinger',
  'paper_swinger_once',
  'paper_popup',
]);

const STEEL_TARGETS = new Set(['steel_target', 'popper', 'plate', 'texas_star']);

const styles = StyleSheet.create({
  page: {
    padding: 28,
    fontFamily: 'Helvetica',
    fontSize: 9,
    color: '#111827',
  },
  header: {
    borderBottomWidth: 1.5,
    borderBottomColor: '#111827',
    paddingBottom: 10,
    marginBottom: 12,
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    marginBottom: 4,
  },
  competitionName: {
    fontSize: 12,
    fontWeight: 700,
    marginBottom: 3,
    color: '#374151',
  },
  subtitle: {
    fontSize: 8,
    color: '#4b5563',
    marginBottom: 2,
  },
  logoBox: {
    width: 96,
    height: 54,
    borderWidth: 1,
    borderColor: '#9ca3af',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: 6,
  },
  logoText: {
    fontSize: 7,
    color: '#6b7280',
    textTransform: 'uppercase',
  },
  badge: {
    borderWidth: 1,
    borderColor: '#111827',
    padding: 6,
    minWidth: 96,
  },
  badgeLabel: {
    fontSize: 6.5,
    textTransform: 'uppercase',
    color: '#6b7280',
    marginBottom: 2,
  },
  badgeValue: {
    fontSize: 16,
    fontWeight: 700,
  },
  section: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 9,
    fontWeight: 700,
    textTransform: 'uppercase',
    color: '#111827',
    backgroundColor: '#f3f4f6',
    borderWidth: 1,
    borderColor: '#d1d5db',
    paddingHorizontal: 6,
    paddingVertical: 4,
    marginBottom: 5,
  },
  selectedImageFrame: {
    borderWidth: 1,
    borderColor: '#111827',
    padding: 6,
    backgroundColor: '#ffffff',
  },
  selectedImage: {
    width: '100%',
    height: 235,
    objectFit: 'contain',
  },
  blueprintImage: {
    width: '100%',
    height: 410,
    objectFit: 'contain',
  },
  note: {
    color: '#374151',
    lineHeight: 1.35,
  },
  row: {
    flexDirection: 'row',
    gap: 10,
  },
  column: {
    flex: 1,
  },
  summaryGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: '#d1d5db',
  },
  summaryCell: {
    width: '25%',
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#d1d5db',
    padding: 6,
    minHeight: 38,
  },
  summaryLabel: {
    fontSize: 6.5,
    color: '#6b7280',
    textTransform: 'uppercase',
    marginBottom: 3,
  },
  summaryValue: {
    fontSize: 12,
    fontWeight: 700,
  },
  table: {
    borderTopWidth: 1,
    borderLeftWidth: 1,
    borderColor: '#d1d5db',
  },
  tr: {
    flexDirection: 'row',
  },
  td: {
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: '#d1d5db',
    paddingHorizontal: 4,
    paddingVertical: 3.5,
    fontSize: 7.5,
  },
  th: {
    backgroundColor: '#f9fafb',
    fontWeight: 700,
  },
  textBlock: {
    borderWidth: 1,
    borderColor: '#d1d5db',
    padding: 7,
    lineHeight: 1.35,
    minHeight: 58,
  },
  footer: {
    position: 'absolute',
    left: 28,
    right: 28,
    bottom: 18,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    paddingTop: 5,
    flexDirection: 'row',
    justifyContent: 'space-between',
    color: '#6b7280',
    fontSize: 7,
  },
});

interface StagePackageProps {
  layout: StageLayout;
  selectedViewDataUrl: string;
  blueprintImageDataUrl?: string;
  matchName?: string;
  pageSize: 'A4' | 'LETTER';
  sourceView: '2d' | '3d';
}

function formatEnum(value?: string): string {
  if (!value) return 'N/A';
  return value.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function getObjectLabel(object: StageObject, index: number): string {
  if (object.label?.trim()) return object.label.trim();
  if (object.type === 'no_shoot') return `NS${index + 1}`;
  if (STEEL_TARGETS.has(object.type)) return object.type === 'popper' ? `P${index + 1}` : `S${index + 1}`;
  return `T${index + 1}`;
}

function getRequiredShots(object: StageObject): number | null {
  if (object.type === 'no_shoot') return null;
  if (typeof object.points === 'number' && object.points > 0) return object.points;
  if (PAPER_TARGETS.has(object.type)) return 2;
  if (STEEL_TARGETS.has(object.type)) return 1;
  return null;
}

function getTargetName(object: StageObject): string {
  if (object.type === 'no_shoot') return 'No-shoot';
  return formatEnum(object.type);
}

function buildCountSummary(layout: StageLayout): CountSummary {
  let paper = 0;
  let steel = 0;
  let noShoots = 0;
  let movers = 0;
  let requiredShots = 0;

  const targetObjects = layout.objects.filter((object) =>
    PAPER_TARGETS.has(object.type) || STEEL_TARGETS.has(object.type) || object.type === 'no_shoot',
  );

  const targetRows = targetObjects.map((object, index) => {
    const shots = getRequiredShots(object);
    if (object.type === 'no_shoot') noShoots += 1;
    if (PAPER_TARGETS.has(object.type)) paper += 1;
    if (STEEL_TARGETS.has(object.type)) steel += 1;
    if (MOVING_TARGETS.has(object.type)) movers += 1;
    if (shots) requiredShots += shots;

    const notes = [
      STEEL_TARGETS.has(object.type) ? 'Must fall' : '',
      MOVING_TARGETS.has(object.type) ? 'Static export position selected in designer' : '',
      object.scoring_zone && object.scoring_zone !== 'full'
        ? formatEnum(object.scoring_zone)
        : '',
    ].filter(Boolean).join('; ');

    return {
      id: getObjectLabel(object, index),
      target: getTargetName(object),
      shots: shots === null ? '-' : String(shots),
      notes: notes || '-',
    };
  });

  return { paper, steel, noShoots, movers, requiredShots, targetRows };
}

function Header({
  layout,
  matchName,
  summary,
  pageLabel,
}: {
  layout: StageLayout;
  matchName?: string;
  summary: CountSummary;
  pageLabel: string;
}) {
  return (
    <View style={styles.header}>
      <View style={{ flex: 1, paddingRight: 14 }}>
        <Text style={styles.title}>{layout.metadata.name || 'Untitled Stage'}</Text>
        <Text style={styles.competitionName}>
          {matchName?.trim() || 'Competition name'}
        </Text>
        <Text style={styles.subtitle}>Discipline: {formatEnum(layout.metadata.discipline)}</Text>
        <Text style={styles.subtitle}>Scoring: {formatEnum(layout.metadata.scoring_type)}</Text>
      </View>
      <View style={{ alignItems: 'stretch' }}>
        <View style={styles.logoBox}>
          <Text style={styles.logoText}>Organizer logo</Text>
        </View>
        <View style={styles.badge}>
          <Text style={styles.badgeLabel}>{pageLabel}</Text>
          <Text style={styles.badgeValue}>{summary.requiredShots}</Text>
          <Text style={styles.subtitle}>minimum rounds</Text>
        </View>
      </View>
    </View>
  );
}

function SummaryCell({ label, value }: { label: string; value: string | number }) {
  return (
    <View style={styles.summaryCell}>
      <Text style={styles.summaryLabel}>{label}</Text>
      <Text style={styles.summaryValue}>{String(value)}</Text>
    </View>
  );
}

function SummaryGrid({ layout, summary }: { layout: StageLayout; summary: CountSummary }) {
  const scoreBasis = layout.metadata.discipline === 'idpa' ? 'Points down' : 'Points';

  return (
    <View style={styles.summaryGrid}>
      <SummaryCell label="Discipline" value={formatEnum(layout.metadata.discipline)} />
      <SummaryCell label="Scoring" value={formatEnum(layout.metadata.scoring_type)} />
      <SummaryCell label="Paper" value={summary.paper} />
      <SummaryCell label="Steel" value={summary.steel} />
      <SummaryCell label="No-shoots" value={summary.noShoots} />
      <SummaryCell label="Movers" value={summary.movers} />
      <SummaryCell label="Min rounds" value={summary.requiredShots} />
      <SummaryCell label="Score basis" value={scoreBasis} />
    </View>
  );
}

function TargetTable({ rows }: { rows: TargetRow[] }) {
  const widths = ['12%', '30%', '14%', '44%'];
  const headers = ['ID', 'Target', 'Shots', 'Notes'];
  const displayRows = rows.length > 0
    ? rows
    : [{ id: '-', target: 'No scoring targets', shots: '-', notes: '-' }];

  return (
    <View style={styles.table}>
      <View style={styles.tr}>
        {headers.map((header, index) => (
          <Text key={header} style={[styles.td, styles.th, { width: widths[index] }]}>
            {header}
          </Text>
        ))}
      </View>
      {displayRows.map((row) => (
        <View style={styles.tr} key={`${row.id}-${row.target}`}>
          <Text style={[styles.td, { width: widths[0] }]}>{row.id}</Text>
          <Text style={[styles.td, { width: widths[1] }]}>{row.target}</Text>
          <Text style={[styles.td, { width: widths[2] }]}>{row.shots}</Text>
          <Text style={[styles.td, { width: widths[3] }]}>{row.notes}</Text>
        </View>
      ))}
    </View>
  );
}

function Footer({ pageNumber, totalPages }: { pageNumber: number; totalPages: number }) {
  return (
    <View style={styles.footer}>
      <Text>Generated by HitFactor.pro Stage Designer</Text>
      <Text>Page {pageNumber} of {totalPages}</Text>
    </View>
  );
}

const StagePackage: React.FC<StagePackageProps> = ({
  layout,
  selectedViewDataUrl,
  blueprintImageDataUrl,
  matchName,
  pageSize,
  sourceView,
}) => {
  const summary = buildCountSummary(layout);
  const selectedViewTitle = sourceView === '3d'
    ? 'Selected 3D View From Stage Designer'
    : 'Selected Blueprint View From Stage Designer';
  const selectedViewNote = sourceView === '3d'
    ? 'Camera angle, zoom, labels, and moving-target position are selected in the export dialog before generating this printable package.'
    : 'This top-down blueprint image is selected in the export dialog before generating this printable package.';
  const blueprintImage = blueprintImageDataUrl || selectedViewDataUrl;

  return (
    <Document>
      <Page size={pageSize} style={styles.page}>
        <Header
          layout={layout}
          matchName={matchName}
          summary={summary}
          pageLabel="Stage summary"
        />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>{selectedViewTitle}</Text>
          <View style={styles.selectedImageFrame}>
            {selectedViewDataUrl ? (
              <Image src={selectedViewDataUrl} style={styles.selectedImage} />
            ) : (
              <Text style={styles.note}>No selected view available.</Text>
            )}
          </View>
          <Text style={[styles.note, { marginTop: 4 }]}>{selectedViewNote}</Text>
        </View>

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Stage Summary</Text>
          <SummaryGrid layout={layout} summary={summary} />
        </View>

        <View style={styles.row}>
          <View style={styles.column}>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Briefing</Text>
              <Text style={styles.textBlock}>
                {layout.metadata.briefing?.trim() || 'Add stage briefing text in the designer before export.'}
              </Text>
            </View>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Procedure And Mechanics</Text>
              <Text style={styles.textBlock}>
                {layout.metadata.discipline === 'idpa'
                  ? 'Scoring: time plus points down and penalties. Steel must fall to score. Concealment garment, start condition, cover use, and other procedure notes should be specified in the briefing.'
                  : 'Scoring and procedure follow the selected discipline and scoring type. Steel must fall when applicable. Add stage-specific procedure notes in the briefing.'}
              </Text>
            </View>
          </View>

          <View style={styles.column}>
            <View style={styles.section}>
              <Text style={styles.sectionTitle}>Target And Shot Count</Text>
              <TargetTable rows={summary.targetRows} />
            </View>
          </View>
        </View>

        <Footer pageNumber={1} totalPages={2} />
      </Page>

      <Page size={pageSize} style={styles.page}>
        <Header
          layout={layout}
          matchName={matchName}
          summary={summary}
          pageLabel="Blueprint"
        />

        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Construction Blueprint</Text>
          <View style={styles.selectedImageFrame}>
            {blueprintImage ? (
              <Image src={blueprintImage} style={styles.blueprintImage} />
            ) : (
              <Text style={styles.note}>No blueprint image available.</Text>
            )}
          </View>
        </View>

        <View style={styles.row}>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Required Shots</Text>
            <TargetTable rows={summary.targetRows} />
          </View>
          <View style={styles.column}>
            <Text style={styles.sectionTitle}>Build Notes</Text>
            <Text style={styles.textBlock}>
              Confirm wall placement, target visibility, safe angles, steel reset access, and moving-target export positions on the range before building the final stage.
            </Text>
          </View>
        </View>

        <Footer pageNumber={2} totalPages={2} />
      </Page>
    </Document>
  );
};

export async function generatePdfBriefing(
  layout: StageLayout,
  diagramImageDataUrl: string,
  options: PdfOptions,
): Promise<Blob> {
  const doc = (
    <StagePackage
      layout={layout}
      selectedViewDataUrl={diagramImageDataUrl}
      blueprintImageDataUrl={options.blueprintImageDataUrl}
      matchName={options.matchName}
      pageSize={options.pageSize}
      sourceView={options.sourceView ?? '2d'}
    />
  );

  return pdf(doc).toBlob();
}

export function downloadPdf(blob: Blob, filename: string): void {
  downloadBlob(blob, filename);
}
